import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
	afterRenderEffect,
	ChangeDetectionStrategy,
	Component,
	computed,
	HostListener,
	inject,
	input,
	output,
	PLATFORM_ID,
	signal,
	viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { DropdownComponent } from '@valk-nx/components/ui-dropdown/src/lib/dropdown';
import { LinkComponent } from '@valk-nx/components/ui-link/src/lib/link';
import type { Link } from '@valk-nx/components/ui-link/src/lib/link.interface';
import { LinkListComponent } from '@valk-nx/components/ui-link-list/src/lib/link-list.component';
import type { LinkListItem } from '@valk-nx/components/ui-link-list/src/lib/link-list.interface';
import { PopoverModule } from '@valk-nx/components/ui-popover/src/lib/popover.module';
import { DropdownButtonComponent } from '@valk-nx/compositions/ui-dropdown-button/src/lib/dropdown-button';
import type { Language } from '@valk-nx/core/lib/core';
import { cn, LANGUAGE } from '@valk-nx/core/lib/core';
import { ClickedDirective } from '@valk-nx/core/lib/directives/clicked/clicked.directive';

import type {
	CtaGroupResolved,
	CtaItem,
	HeaderMenuItem,
	MetaMenu,
} from '../header.interface';
import { isCtaGroupResolved } from '../header.interface';

/** Horizontal gap between menu items, matching the `gap-x-4` / `ml-4` utility (1rem). */
const MENU_ITEM_GAP_PX = 16;
/** Extra room reserved for the "more" trigger so it never gets clipped itself. */
const MORE_BUTTON_BUFFER_PX = 8;
/** Never show more than this many inline items, regardless of available width. */
const MAX_NUMBER_MENU_ITEMS = 7;

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-header-desktop`,
	templateUrl: './header-desktop.html',
	host: { ngSkipHydration: 'true' },
	styleUrls: ['header-desktop.scss'],
	imports: [
		AngularSvgIconModule,
		ClickedDirective,
		CommonModule,
		DropdownButtonComponent,
		DropdownComponent,
		LinkComponent,
		LinkListComponent,
		PopoverModule,
		RouterModule,
		TranslatePipe,
	],
})
export class HeaderDesktopComponent {
	private readonly rootLanguage = inject<Language>(LANGUAGE);
	private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
	private readonly translate = inject(TranslateService);

	title = input.required<string>();
	menuItems = input.required<HeaderMenuItem[]>();
	metaMenu = input.required<MetaMenu>();
	language = input.required<string>();
	isNegativeHeader = input<boolean>(false);
	hideMenu = input<boolean>();
	cta = input<CtaItem | CtaGroupResolved>();

	moreButtonLabelTranslation = toSignal(
		this.translate.stream('compositions.header.more'),
	);

	desktopMenu = viewChild<ElementRef<Element>>('desktopMenu');
	desktopMenuContainer = viewChild<ElementRef<Element>>(
		'desktopMenuContainer',
	);
	moreMenu = viewChild<ElementRef<Element>>('moreMenu');

	private readonly menuWrapper =
		viewChild<ElementRef<HTMLElement>>('menuWrapper');
	private readonly measureList =
		viewChild<ElementRef<HTMLElement>>('measureList');
	private readonly moreSample =
		viewChild<ElementRef<HTMLElement>>('moreSample');

	/** Number of items rendered inline; the rest move into the "more" dropdown. */
	readonly visibleCount = signal(MAX_NUMBER_MENU_ITEMS);

	readonly visibleMenuItems = computed<HeaderMenuItem[]>(() =>
		this.menuItems().slice(0, this.visibleCount()),
	);

	readonly hasOverflow = computed<boolean>(
		() => this.visibleCount() < this.menuItems().length,
	);

	moreMenuItems = computed<LinkListItem[]>(() => {
		const hiddenItems = this.menuItems().slice(this.visibleCount());

		return hiddenItems.map((item) => {
			return {
				url: item.url,
				icon: '',
				text: item.title,
				isExternal: true, // This makes the link list use href instead of routerLink
				targetSelf: true,
			};
		});
	});

	isCtaGroupResolved = computed<CtaGroupResolved | undefined>(() => {
		const cta = this.cta();
		if (!cta) return undefined;
		return isCtaGroupResolved(cta) ? cta : undefined;
	});

	isCtaItem = computed<CtaItem | undefined>(() => {
		const cta = this.cta();
		if (!cta) return undefined;
		return isCtaGroupResolved(cta) ? undefined : cta;
	});

	renderedMenuItems = computed(() => {
		const visible = this.visibleMenuItems();
		const cap =
			visible.length > MAX_NUMBER_MENU_ITEMS
				? MAX_NUMBER_MENU_ITEMS - 1
				: MAX_NUMBER_MENU_ITEMS;

		return visible.slice(0, cap);
	});

	logoLink = computed<Link>(() => ({
		url:
			this.language() === this.rootLanguage ? '/' : `/${this.language()}`,
		title: 'home',
	}));

	storedMenuItems = signal<HeaderMenuItem[]>([]);

	languageSwitch = output<string>();

	showingMoreMenu = false;

	cn = cn;

	constructor() {
		afterRenderEffect(() => {
			if (this.moreButtonLabelTranslation()) {
				this.updateVisibleCount();
			}
		});
	}

	@HostListener('window:resize')
	onResize() {
		this.updateVisibleCount();
	}

	updateVisibleCount(): void {
		if (!this.isBrowser) {
			return;
		}

		const wrapper = this.menuWrapper()?.nativeElement;
		const measureList = this.measureList()?.nativeElement;
		const moreSample = this.moreSample()?.nativeElement;

		if (!wrapper || !measureList || !moreSample) {
			return;
		}

		const itemWidths = Array.from(
			measureList.querySelectorAll<HTMLElement>('[data-measure-item]'),
		).map((element) => element.offsetWidth);

		const items = Array.from(
			measureList.querySelectorAll<HTMLElement>('[data-measure-item]'),
		);
		const gap =
			items.length > 1
				? items[1].getBoundingClientRect().left -
					items[0].getBoundingClientRect().right
				: MENU_ITEM_GAP_PX;

		this.visibleCount.set(
			HeaderDesktopComponent.calculateVisibleCount(
				itemWidths,
				gap,
				wrapper.clientWidth,
				moreSample.offsetWidth + MORE_BUTTON_BUFFER_PX,
				MAX_NUMBER_MENU_ITEMS,
			),
		);
	}

	/**
	 * Determines how many items fit on a single line. When everything fits within
	 * `maxItems` no room is reserved for the "more" trigger; otherwise the trigger
	 * width is reserved so the visible items plus the trigger stay on one line.
	 */
	static calculateVisibleCount(
		itemWidths: number[],
		gap: number,
		available: number,
		moreWidth: number,
		maxItems: number,
	): number {
		const total = itemWidths.length;
		if (total === 0) {
			return 0;
		}

		if (total <= maxItems) {
			const totalWidth =
				itemWidths.reduce((sum, width) => sum + width, 0) +
				gap * (total - 1);
			if (totalWidth <= available) {
				return total;
			}
		}

		let used = 0;
		let count = 0;
		for (let index = 0; index < total && count < maxItems; index++) {
			const itemWidth = (count === 0 ? 0 : gap) + itemWidths[index];
			if (used + itemWidth + gap + moreWidth <= available) {
				used += itemWidth;
				count++;
			} else {
				break;
			}
		}

		return count;
	}
}
